/**
 * Pure sizing / stacking rules for the map cluster donut markers.
 * No Leaflet import so Jest can load this module.
 */

export const MIN_DONUT_SIZE = 40;
export const MAX_DONUT_SIZE = 80;

/** Donut diameter in px: 40 px for a single asset, +12 px per decade, capped at 80 px. */
export const getDonutSize = (count: number): number =>
  Math.min(MIN_DONUT_SIZE + Math.log10(Math.max(count, 1)) * 12, MAX_DONUT_SIZE);

/**
 * Leaflet sets a marker's z-index to its layer-point y plus `zIndexOffset`. Visible
 * clusters differ by at most ~container height + marker size (~610 px) in y, so a
 * 1000 px step per rank makes the count ordering dominate the position ordering.
 */
export const Z_INDEX_STEP = 1000;

/** `riseOffset` for `riseOnHover`: larger than any rank offset, far below the CSS int32 limit. */
export const CLUSTER_RISE_OFFSET = 10_000_000;

/**
 * `zIndexOffset` per input index: the smallest count gets 0, the largest (n-1)·step,
 * so bigger clusters always render above smaller ones. Ties keep input order.
 */
export const getClusterZIndexOffsets = (counts: number[]): number[] => {
  const order = counts
    .map((count, index) => ({ count, index }))
    .sort((a, b) => a.count - b.count || a.index - b.index);
  const offsets = new Array<number>(counts.length);
  order.forEach(({ index }, rank) => {
    offsets[index] = rank * Z_INDEX_STEP;
  });
  return offsets;
};

/**
 * Font size for the count label: a quarter of the donut (min 10 px), shrunk only when
 * the label would not fit 85 % of the inner disc (0.6·size wide, ~0.6 em per bold digit).
 */
export const getCountLabelFontSize = (size: number, labelLength: number): number => {
  const base = Math.max(size * 0.25, 10);
  const maxByWidth = (size * 0.6 * 0.85) / (0.6 * Math.max(labelLength, 1));
  return Math.max(9, Math.min(base, maxByWidth));
};
